import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useQuery } from 'react-query';
import { FiUser, FiUsers, FiHash } from 'react-icons/fi';
import { BsBuildings } from 'react-icons/bs';

import { Text, textVariantsEnum, fontWeightEnum } from '@/components/Text';
import { Button, buttonVariants } from '@/components/Button';
import { Dropdown, borderedSelectStyles } from '@/components/Dropdown';
import { Input } from '@/components/Input';
import { CopyIconButton } from '@components/CopyIconButton';
import {
  SettingsFieldCard,
  settingsFieldCardLayouts,
  SETTINGS_FIELD_CONTROL_WIDTH as FIELD_CONTROL_WIDTH,
} from '@/views/SettingsRedesign/components/SettingsFieldCard.component';
import { addNotification, notificationStates } from '@/components/Notification';
import { useFormChanges } from '@/hooks/useFormChanges';
import { teamActionTypes } from '@/store/Team/Team.actionTypes';
import { authActionTypes } from '@/store/Auth/Auth.actionTypes';
import teamDatabase from '@/services/team';
import authService from '@/services/auth';
import { fetchEmployeeExtendedDetails } from '@/services/employeeMemberEnrichment';
import { queryKeys } from '@/config/queryKeys';
import { db } from '@/config/firebase.config';
import { IS_DEMO_MODE } from '@/utils/demoMode';
import {
  DEMO_MY_EXTENDED_DETAILS,
  DEMO_TEAM_OPTIONS,
} from '@/views/EmployeeList/EmployeeList.demoData';

import styles from './ProfileEditForm.module.scss';

const buildCompanyOption = (activeCompany) =>
  activeCompany
    ? {
        ...activeCompany,
        value: activeCompany.companyId,
        label: activeCompany.companyName,
      }
    : null;

const buildActiveTeamOption = (activeTeam) =>
  activeTeam?.activeTeamId
    ? { ...activeTeam, value: activeTeam.activeTeamId, label: activeTeam.Team }
    : null;

export const ProfileEditForm = () => {
  const dispatch = useDispatch();
  const [editableName, setEditableName] = useState('');
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [selectedActiveTeam, setSelectedActiveTeam] = useState(null);
  const [saving, setSaving] = useState(false);

  const user = useSelector((state) => ({
    userProfile: state.auth?.profile,
    activeCompany: state.auth?.profile?.activeCompany,
    companyList: state.auth?.profile?.companyList,
  }));
  const team = useSelector((state) => ({
    activeTeam: state.team.activeTeam,
    teams: state.team.teams,
  }));

  const userId = user.userProfile?.id;
  const {
    data: realExtendedDetails,
    isLoading: realLoadingGuid,
    isError: realGuidError,
  } = useQuery(
    queryKeys.employeeList.extendedDetails(userId),
    () => fetchEmployeeExtendedDetails(db, userId),
    { enabled: !!userId && !IS_DEMO_MODE, staleTime: 5 * 60 * 1000 }
  );
  const extendedDetails = IS_DEMO_MODE
    ? DEMO_MY_EXTENDED_DETAILS
    : realExtendedDetails;
  const loadingGuid = IS_DEMO_MODE ? false : realLoadingGuid;
  const guidError = IS_DEMO_MODE ? false : realGuidError;

  const guidDisplay =
    guidError || !extendedDetails
      ? '—'
      : extendedDetails.guid === 'N/A' || !extendedDetails.guid
        ? 'N/A'
        : extendedDetails.guid;
  const guidCopyable = guidDisplay !== '—' && guidDisplay !== 'N/A';

  const companyOptions = useMemo(
    () => user.companyList?.map(buildCompanyOption) || [],
    [user.companyList]
  );
  const realTeamOptions = useMemo(
    () =>
      team.teams?.data?.map((t) => ({
        ...t,
        value: t?.activeTeamid,
        label: t?.Team,
      })) || [],
    [team.teams?.data]
  );
  const teamOptions = IS_DEMO_MODE ? DEMO_TEAM_OPTIONS : realTeamOptions;

  useEffect(() => {
    setEditableName(user.userProfile?.name || '');
  }, [user.userProfile?.name]);
  useEffect(() => {
    setSelectedCompany(buildCompanyOption(user.activeCompany));
  }, [user.activeCompany]);
  useEffect(() => {
    setSelectedActiveTeam(
      IS_DEMO_MODE
        ? DEMO_TEAM_OPTIONS.find(
            (o) => o.value === DEMO_MY_EXTENDED_DETAILS.activeTeamId
          )
        : buildActiveTeamOption(team.activeTeam)
    );
  }, [team.activeTeam]);

  const { hasChanges: hasNameChanges } = useFormChanges(
    { name: user.userProfile?.name },
    { name: editableName },
    ['name']
  );
  const { hasChanges: hasCompanyChanges } = useFormChanges(
    { companyId: user.activeCompany?.companyId },
    { companyId: selectedCompany?.value },
    ['companyId']
  );
  const { hasChanges: hasActiveTeamChanges } = useFormChanges(
    {
      activeTeamId: IS_DEMO_MODE
        ? DEMO_MY_EXTENDED_DETAILS.activeTeamId
        : team.activeTeam?.activeTeamId,
    },
    { activeTeamId: selectedActiveTeam?.value },
    ['activeTeamId']
  );
  const hasAnyChanges =
    hasNameChanges || hasCompanyChanges || hasActiveTeamChanges;

  const handleGuidCopied = () => {
    addNotification({
      message: 'GUID copied to clipboard',
      level: notificationStates.success,
    });
  };

  const handleGuidCopyError = () => {
    addNotification({
      message: 'Could not copy GUID — select the text and copy manually.',
      level: notificationStates.warning,
    });
  };

  const saveName = async () => {
    const trimmedName = String(editableName ?? '').trim();
    if (!trimmedName) {
      addNotification({
        message: 'Name cannot be empty',
        level: notificationStates.warning,
      });
      return;
    }

    if (IS_DEMO_MODE) {
      dispatch({
        type: authActionTypes.SET_STATE,
        payload: { profile: { ...user.userProfile, name: trimmedName } },
      });
      addNotification({
        message: 'Name updated successfully',
        level: notificationStates.success,
      });
      return;
    }

    const response = await authService.updateUserNameEverywhere(trimmedName);
    if (response?.success) {
      dispatch({
        type: authActionTypes.SET_STATE,
        payload: { profile: { ...user.userProfile, name: trimmedName } },
      });
      addNotification({
        message: 'Name updated successfully',
        level: notificationStates.success,
      });
    } else {
      addNotification({
        message: response?.data || 'Failed to update name',
        level: notificationStates.error,
      });
    }
  };

  const saveCompany = () => {
    if (IS_DEMO_MODE) {
      addNotification({
        message: 'Switching companies is not available in demo mode.',
        level: notificationStates.info,
      });
      return;
    }
    dispatch({
      type: teamActionTypes.SWITCH_COMPANY_ACTIVE_TEAM,
      payload: {
        companyId: selectedCompany.value,
        userId: user.userProfile?.id,
      },
    });
  };

  const saveActiveTeam = async () => {
    const teamDocId =
      selectedActiveTeam?.activeTeamid ?? selectedActiveTeam?.teamId;
    const teamLabel =
      selectedActiveTeam?.Team ??
      selectedActiveTeam?.teamName ??
      selectedActiveTeam?.label ??
      '';

    if (IS_DEMO_MODE) {
      addNotification({
        message: `Active department saved: ${teamLabel}`,
        level: notificationStates.success,
      });
      return;
    }

    const cid = user.activeCompany?.companyId;
    const uid = user.userProfile?.id;
    if (!teamDocId || !cid || !uid) return;

    const updateRes = await teamDatabase.updateMemberActiveCompanyAndTeam(
      uid,
      cid,
      teamDocId
    );
    if (updateRes?.success) {
      addNotification({
        message: `Active department saved: ${teamLabel}`,
        level: notificationStates.success,
      });
      const emailRes = await teamDatabase.sendActiveTeamChangeEmail({
        companyName: user.activeCompany?.companyName || '',
        teamName: teamLabel,
      });
      if (!emailRes?.success) {
        addNotification({
          message:
            'Active department saved, but the confirmation email could not be sent.',
          level: notificationStates.warning,
        });
      }
    } else {
      addNotification({
        message:
          updateRes?.error?.message ||
          'Could not save active department. Check permissions and try again.',
        level: notificationStates.error,
      });
    }
  };

  const handleSaveChanges = async () => {
    setSaving(true);
    try {
      if (hasNameChanges) await saveName();
      if (hasCompanyChanges) saveCompany();
      if (hasActiveTeamChanges) await saveActiveTeam();
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    setEditableName(user.userProfile?.name || '');
    setSelectedCompany(buildCompanyOption(user.activeCompany));
    setSelectedActiveTeam(buildActiveTeamOption(team.activeTeam));
  };

  return (
    <div className={styles.editForm}>
      <div className={styles.editFormHeader}>
        <Text
          variant={textVariantsEnum.Text_lg}
          fontWeight={fontWeightEnum.Semibold}
          color="gray-900"
        >
          Edit Profile
        </Text>
        <Text
          variant={textVariantsEnum.Text_sm}
          fontWeight={fontWeightEnum.Normal}
          color="gray-600"
        >
          Update your personal information and account preferences.
        </Text>
      </div>

      <div className={styles.editFormFields}>
        <SettingsFieldCard
          layout={settingsFieldCardLayouts.row}
          icon={<FiUser size={16} color="var(--indigo-600)" />}
          label="Name"
          helperText="This is how your name will appear."
        >
          <Input
            value={editableName}
            onChange={(e) => setEditableName(e?.target?.value || '')}
            disabled={saving}
            style={{ width: FIELD_CONTROL_WIDTH }}
          />
        </SettingsFieldCard>

        <SettingsFieldCard
          layout={settingsFieldCardLayouts.row}
          icon={<BsBuildings size={16} color="var(--purple-600)" />}
          iconBg="purple-100"
          label="Active Company"
          helperText="This is your currently active company."
        >
          <Dropdown
            options={companyOptions}
            value={selectedCompany}
            width={FIELD_CONTROL_WIDTH}
            isDisabled={saving}
            onChange={setSelectedCompany}
            selectStyles={borderedSelectStyles}
            placeholder="Not set — select a company"
          />
        </SettingsFieldCard>

        <SettingsFieldCard
          layout={settingsFieldCardLayouts.row}
          icon={<FiUsers size={16} color="var(--blue-600)" />}
          iconBg="blue-100"
          label="Active Department (Tracking)"
          helperText="Used for time tracking and reports."
        >
          <Dropdown
            options={teamOptions}
            value={selectedActiveTeam}
            width={FIELD_CONTROL_WIDTH}
            isDisabled={saving}
            onChange={setSelectedActiveTeam}
            selectStyles={borderedSelectStyles}
            placeholder="Not set — select a department"
          />
        </SettingsFieldCard>

        <SettingsFieldCard
          layout={settingsFieldCardLayouts.row}
          icon={<FiHash size={16} color="var(--gray-600)" />}
          iconBg="gray-100"
          label="GUID"
          helperText="Unique identifier for your account."
        >
          <Input
            value={loadingGuid ? '…' : guidDisplay}
            readOnly
            style={{ width: FIELD_CONTROL_WIDTH }}
            rightBlock={
              guidCopyable ? (
                <CopyIconButton
                  value={guidDisplay}
                  label="GUID"
                  onCopied={handleGuidCopied}
                  onError={handleGuidCopyError}
                />
              ) : undefined
            }
          />
        </SettingsFieldCard>
      </div>

      <div className={styles.editFormActions}>
        <Button
          text="Cancel"
          variant={buttonVariants.secondary}
          onClick={handleCancel}
          disabled={saving}
        />
        <Button
          text="Save Changes"
          variant={buttonVariants.primary}
          onClick={handleSaveChanges}
          loading={saving}
          disabled={!hasAnyChanges}
        />
      </div>
    </div>
  );
};

ProfileEditForm.displayName = 'ProfileEditForm';
